import type { CartOrderType } from '@order-up/models';

/**
 * The fulfilment types that can be SCHEDULED or CONFIGURED by the guest.
 *
 * `OrderModal` collects a pickup time or a delivery address; `ScheduleModal`
 * offers time slots for one of those two. Neither means anything to a guest who
 * is already sitting in the dining room — there is no time to arrive and no
 * address to drive to — which is why the table page renders no pickup/delivery
 * affordance at all (Guest QR Platform, piece 4).
 */
export type ScheduledFulfilment = 'pickup' | 'delivery';

/**
 * Narrow a cart's fulfilment for a modal that only handles pickup and delivery.
 *
 * **This is a narrowing, not a conversion.** `dineIn` has no correct answer
 * here, so it returns `pickup` — the same value the call sites already fell
 * back to for an absent cart — and that is a LAST RESORT, not a mapping anyone
 * should rely on. A dine-in cart reaching one of these modals is a caller bug:
 * the fix is at the render site, which must not open them in table mode.
 *
 * It exists as a named function rather than three inline `as` casts because a
 * cast asserts the value cannot be `dineIn`, which is false and silently stays
 * false; this asserts nothing and puts the assumption somewhere greppable. If a
 * dine-in guest is ever meant to schedule something, that is a new modal with
 * its own UI, not a third branch inside these two.
 */
export function toScheduledFulfilment(
  orderType: CartOrderType | undefined,
): ScheduledFulfilment {
  return orderType === 'delivery' ? 'delivery' : 'pickup';
}
